import * as React from "react";
import { Modal, Button, Input, FormGroup, Label } from "../components/Modal";
import * as EmailStore from "../email/EmailStore";
import * as SMSStore from "../sms/SMSStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";

const OrganizationCommunication = ({ organization }) => {
    // Email state
    const [emailTo, setEmailTo] = React.useState("");
    const [emailSubject, setEmailSubject] = React.useState("");
    const [emailText, setEmailText] = React.useState("");
    const [emailError, setEmailError] = React.useState<string | undefined>(undefined);
    const [emailSent, setEmailSent] = React.useState<string | undefined>(undefined);
    const [emailSending, setEmailSending] = React.useState(false);

    // SMS state
    const [smsTo, setSmsTo] = React.useState("");
    const [smsText, setSmsText] = React.useState("");
    const [smsError, setSmsError] = React.useState<string | undefined>(undefined);
    const [smsSent, setSmsSent] = React.useState<string | undefined>(undefined);
    const [smsSending, setSmsSending] = React.useState(false);

    // Message state
    const [messageTo, setMessageTo] = React.useState("");
    const [messageText, setMessageText] = React.useState("");
    const [messageError, setMessageError] = React.useState<string | undefined>(undefined);
    const [messageSent, setMessageSent] = React.useState<string | undefined>(undefined);
    const [messageSending, setMessageSending] = React.useState(false);

    const sendEmail = () => {
        setEmailSending(true);
        setEmailError(undefined);
        setEmailSent(undefined);

        EmailStore.SendEmail(emailTo, emailSubject, emailText)
            .then((response) => {
                if (response.status == 200) {
                    setEmailSent("sent");
                    // Clear form on success
                    setEmailTo("");
                    setEmailSubject("");
                    setEmailText("");
                } else {
                    setEmailSent("error");
                    setEmailError(JSON.stringify(response.data));
                }
            })
            .catch((error) => {
                setEmailSent("error");
                setEmailError(error.message || "Email sending failed");
            })
            .finally(() => {
                setEmailSending(false);
            });
    };

    const sendSMS = () => {
        setSmsSending(true);
        setSmsError(undefined);
        setSmsSent(undefined);

        SMSStore.SendSMS(smsTo, smsText)
            .then((response) => {
                if (response.status == 200) {
                    setSmsSent("sent");
                    // Clear form on success
                    setSmsTo("");
                    setSmsText("");
                } else {
                    setSmsSent("error");
                    setSmsError(JSON.stringify(response.data));
                }
            })
            .catch((error) => {
                setSmsSent("error");
                setSmsError(error.message || "SMS sending failed");
            })
            .finally(() => {
                setSmsSending(false);
            });
    };

    const sendMessage = () => {
        setMessageSending(true);
        setMessageError(undefined);
        setMessageSent(undefined);

        axios
            .post(`/api/messaging/sendMessage`, {
                to: [messageTo],
                text: messageText,
            })
            .then((response) => {
                if (response.data.success) {
                    setMessageSent("sent");
                    // Clear form on success
                    setMessageTo("");
                    setMessageText("");
                } else {
                    setMessageSent("error");
                    setMessageError("Message sending failed");
                }
            })
            .catch((error) => {
                setMessageSent("error");
                setMessageError(error.message || "Message sending failed");
            })
            .finally(() => {
                setMessageSending(false);
            });
    };

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                <FontAwesomeIcon icon={["fad", "paper-plane"]} className="text-3xl text-blue-500" />
                <div>
                    <h2 className="text-2xl font-semibold text-gray-800">Communication Testing</h2>
                    <p className="text-sm text-gray-600">Test integrated email, SMS, and messaging services</p>
                </div>
            </div>

            {/* Three Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Email Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                        <div className="flex items-center space-x-3 text-white">
                            <FontAwesomeIcon icon={["fad", "envelope"]} className="text-2xl" />
                            <div>
                                <h3 className="text-lg font-semibold">Email</h3>
                                <p className="text-sm text-blue-100">Send test email</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        <FormGroup>
                            <Label htmlFor="emailTo">To (Email Address)</Label>
                            <Input id="emailTo" type="email" placeholder="recipient@example.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="emailFrom">From</Label>
                            <Input id="emailFrom" type="text" value="noreply@scadco.co.za" readOnly className="bg-gray-50 cursor-not-allowed" />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="emailSubject">Subject</Label>
                            <Input id="emailSubject" type="text" placeholder="Email subject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="emailText">Message</Label>
                            <Input id="emailText" type="textarea" placeholder="Email content..." rows={5} value={emailText} onChange={(e) => setEmailText(e.target.value)} />
                        </FormGroup>

                        <Button variant="primary" onClick={sendEmail} disabled={!emailTo || !emailSubject || !emailText || emailSending} className="w-full">
                            {emailSending ? (
                                <>
                                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={["fad", "paper-plane"]} className="mr-2" />
                                    Send Email
                                </>
                            )}
                        </Button>

                        {emailSent == "error" && emailError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-start">
                                    <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="text-red-600 mr-2 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-red-800">Email sending failed</p>
                                        <p className="text-xs text-red-600 mt-1">{emailError}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {emailSent == "sent" && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                                    <p className="text-sm font-medium text-green-800">Email sent successfully!</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* SMS Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                        <div className="flex items-center space-x-3 text-white">
                            <FontAwesomeIcon icon={["fad", "mobile"]} className="text-2xl" />
                            <div>
                                <h3 className="text-lg font-semibold">SMS</h3>
                                <p className="text-sm text-green-100">Send test SMS</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        <FormGroup>
                            <Label htmlFor="smsTo">To (Phone Number)</Label>
                            <Input id="smsTo" type="tel" placeholder="+27123456789" value={smsTo} onChange={(e) => setSmsTo(e.target.value)} />
                            <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +27)</p>
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="smsText">Message</Label>
                            <Input id="smsText" type="textarea" placeholder="SMS content..." rows={8} value={smsText} onChange={(e) => setSmsText(e.target.value)} />
                            <p className="text-xs text-gray-500 mt-1">{smsText.length} / 160 characters</p>
                        </FormGroup>

                        <Button variant="success" onClick={sendSMS} disabled={!smsTo || !smsText || smsSending} className="w-full">
                            {smsSending ? (
                                <>
                                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={["fad", "mobile"]} className="mr-2" />
                                    Send SMS
                                </>
                            )}
                        </Button>

                        {smsSent == "error" && smsError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-start">
                                    <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="text-red-600 mr-2 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-red-800">SMS sending failed</p>
                                        <p className="text-xs text-red-600 mt-1">{smsError}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {smsSent == "sent" && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                                    <p className="text-sm font-medium text-green-800">SMS sent successfully!</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Message Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                        <div className="flex items-center space-x-3 text-white">
                            <FontAwesomeIcon icon={["fad", "comment-dots"]} className="text-2xl" />
                            <div>
                                <h3 className="text-lg font-semibold">Message</h3>
                                <p className="text-sm text-purple-100">Send in-app message</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        <FormGroup>
                            <Label htmlFor="messageTo">To (User ID)</Label>
                            <Input id="messageTo" type="text" placeholder="User ID or identifier" value={messageTo} onChange={(e) => setMessageTo(e.target.value)} />
                            <p className="text-xs text-gray-500 mt-1">Enter the user ID to send message to</p>
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="messageText">Message</Label>
                            <Input id="messageText" type="textarea" placeholder="Type your message..." rows={8} value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                        </FormGroup>

                        <Button
                            variant="primary"
                            onClick={sendMessage}
                            disabled={!messageTo || !messageText || messageSending}
                            className="w-full"
                            style={{ backgroundColor: "#9333ea", borderColor: "#9333ea" }}
                        >
                            {messageSending ? (
                                <>
                                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={["fad", "comment-dots"]} className="mr-2" />
                                    Send Message
                                </>
                            )}
                        </Button>

                        {messageSent == "error" && messageError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-start">
                                    <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="text-red-600 mr-2 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-red-800">Message sending failed</p>
                                        <p className="text-xs text-red-600 mt-1">{messageError}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {messageSent == "sent" && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-600 mr-2" />
                                    <p className="text-sm font-medium text-green-800">Message sent successfully!</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Help Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                    <FontAwesomeIcon icon={["fad", "info-circle"]} className="text-blue-600 text-xl mt-0.5" />
                    <div>
                        <h4 className="text-sm font-semibold text-blue-900 mb-2">Testing Communication Services</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>
                                • <strong>Email:</strong> Test email delivery to any email address
                            </li>
                            <li>
                                • <strong>SMS:</strong> Send SMS to mobile numbers (include country code)
                            </li>
                            <li>
                                • <strong>Message:</strong> Send in-app notifications to users by their ID
                            </li>
                        </ul>
                        <p className="text-xs text-blue-700 mt-3">Note: All communications are sent through the organization's configured services.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrganizationCommunication;
